import { useFormik } from "formik"
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap"
import { toast } from "react-toastify"

import InputField from "components/Common/InputField"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import { formatUiDateTime } from "utils/formatDate"
import { useAuth } from "hooks/authHook"
import { ACCESS } from "constants/accessPermissions"
import {
  useComplaint,
  useComplaintMeta,
  useUpdateComplaintStatus,
} from "hooks/complaintHook"

const titleCase = s =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())

const ComplaintDetailModal = ({ id, isOpen, toggle }) => {
  const { hasPermission } = useAuth()
  const canManage = hasPermission(ACCESS.COMPLAINT.MANAGE)

  const { data: resp, isLoading } = useComplaint(id)
  const complaint = resp?.data

  const { data: metaResp } = useComplaintMeta()
  const statuses = metaResp?.data?.statuses ?? []

  const updateMutation = useUpdateComplaintStatus()

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      status: complaint?.status ?? "open",
      resolution_note: complaint?.resolution_note ?? "",
    },
    onSubmit: async values => {
      try {
        await updateMutation.mutateAsync({
          id,
          status: values.status,
          resolution_note: values.resolution_note || null,
        })
        toast.success("Complaint updated")
        toggle()
      } catch (err) {
        toast.error(err?.response?.data?.message || "Update failed")
      }
    },
  })

  const statusOptions = statuses.map(s => ({ value: s, label: titleCase(s) }))

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
      <ModalHeader toggle={toggle}>Complaint</ModalHeader>
      <form onSubmit={formik.handleSubmit}>
        <ModalBody>
          {isLoading || !complaint ? (
            <div className="text-center py-4">
              <Spinner size="sm" color="primary" />
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h5 className="mb-0">{complaint.subject}</h5>
                <Badge color="light" className="text-dark text-capitalize">
                  {titleCase(complaint.category)}
                </Badge>
              </div>
              <div className="text-muted small mb-3">
                {complaint.is_anonymous
                  ? "Submitted anonymously"
                  : `From ${complaint.employee?.name ?? "—"}`}
                {" · "}
                {formatUiDateTime(complaint.created_at)}
                {complaint.showroom?.name ? ` · ${complaint.showroom.name}` : ""}
              </div>

              <p style={{ whiteSpace: "pre-wrap" }}>{complaint.message}</p>

              {(complaint.images ?? []).length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {complaint.images.map(img => (
                    <a key={img.id} href={img.url} target="_blank" rel="noreferrer">
                      <img
                        src={img.url}
                        alt={img.original_name || "attachment"}
                        style={{ height: 90, borderRadius: 6, objectFit: "cover" }}
                      />
                    </a>
                  ))}
                </div>
              )}

              <hr />

              {canManage ? (
                <>
                  <FormikCreatableSelect
                    label="Status"
                    name="status"
                    formik={formik}
                    options={statusOptions}
                  />
                  <label className="form-label">Resolution / action note</label>
                  <InputField
                    type="textarea"
                    name="resolution_note"
                    value={formik.values.resolution_note}
                    onChange={formik.handleChange}
                    placeholder="Visible to the employee who submitted the complaint"
                  />
                </>
              ) : (
                <>
                  <div>
                    Status:{" "}
                    <Badge color="info">{titleCase(complaint.status)}</Badge>
                  </div>
                  {complaint.resolution_note && (
                    <p className="mt-2 mb-0">
                      <strong>Note:</strong> {complaint.resolution_note}
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={toggle}>
            Close
          </Button>
          {canManage && complaint && (
            <Button color="primary" type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          )}
        </ModalFooter>
      </form>
    </Modal>
  )
}

export default ComplaintDetailModal
